import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { type PropsWithChildren } from "react";
import { IntlProvider } from "use-intl";

import { api } from "@/api/api";
import { getDir } from "@/hooks/useLocaleInfo";

import defaultMessages from "../messages/ar.json";

import { isProduction } from "./app/lib/constants";
import { CacheProvider } from "./app/lib/context/CacheProvider";
import { MuiThemeProvider } from "./app/lib/context/MuiThemeProvider";
import { platformApi } from "./app/lib/platform";
import {
  defaultLocale,
  isAllowedLocale,
  type Locale,
} from "./app/lib/utils/locale";

let locale: Locale = defaultLocale;
let messages = defaultMessages;

// TODO: modify this when we decide to support eng and ar versions on production
if (!isProduction) {
  try {
    const fetchedLocale = await platformApi.getLocale();

    locale = isAllowedLocale(fetchedLocale) ? fetchedLocale : defaultLocale;
    if (locale !== defaultLocale) {
      messages = (await import(`../messages/${locale}.json`)).default;
    }
  } catch (error) {
    Sentry.captureException(error);
    // in case of error, we fallback to en locale for now, as it better for development
    locale = "en";
    if (locale !== defaultLocale) {
      messages = (await import(`../messages/${locale}.json`)).default;
    }
  }
}

api.defaults.headers.common["Accept-Language"] = locale;
dayjs.locale(locale);
document.documentElement.dir = getDir(locale);

export const RouterWrapper = ({ children }: PropsWithChildren) => {
  return (
    <IntlProvider locale={locale} messages={messages}>
      <CacheProvider locale={locale}>
        <MuiThemeProvider>{children}</MuiThemeProvider>
      </CacheProvider>
    </IntlProvider>
  );
};
